/**
 * Controles de un nodo ya generado:
 *
 *   • "Regenerar"            → vuelve al formulario limpiando el resultado
 *                              (el comportamiento histórico).
 *   • "Volver a configurar"  → vuelve al formulario CONSERVANDO el resultado:
 *                              no genera, no consume créditos, no borra el
 *                              asset ni el historial. El resultado activo se
 *                              reemplaza sólo cuando la nueva generación
 *                              termina bien; si falla o se cancela, queda el
 *                              último resultado válido.
 */
import { useEffect, useRef, useState } from 'react';
import { RefreshCw, Settings2, X } from 'lucide-react';

/**
 * Estado de reconfiguración de un nodo.
 * Se cierra solo cuando aparece un resultado NUEVO (ahí sí se reemplaza).
 */
export const useReconfigure = (data) => {
  const [reconfigure, setReconfigure] = useState(false);
  const lastOutput = useRef(data?.outputUrl);

  useEffect(() => {
    if (data?.outputUrl && data.outputUrl !== lastOutput.current) {
      lastOutput.current = data.outputUrl;
      setReconfigure(false);
    }
  }, [data?.outputUrl]);

  return { reconfigure, setReconfigure, showForm: !data?.outputUrl || reconfigure };
};

export const RegenerateButton = ({ onClick, testId, label = 'Regenerar' }) => (
  <button
    type="button"
    className="nano-regen-btn"
    onClick={onClick}
    data-testid={testId}
    title="Regenerar: vuelve al formulario y descarta el resultado actual"
  >
    <RefreshCw size={12} /> {label}
  </button>
);

export const ReconfigureButton = ({ onClick, testId, className = '' }) => (
  <button
    type="button"
    className={`node-reconfigure-btn ${className}`}
    onClick={onClick}
    data-testid={testId}
    title="Volver a configurar: recuperá prompt, referencias y parámetros sin perder el resultado actual"
  >
    <Settings2 size={12} /> Volver a configurar
  </button>
);

export const ReconfigureNotice = ({ previewUrl, onCancel, testId }) => (
  <div className="node-reconfigure-notice" data-testid={testId}>
    {previewUrl && <img src={previewUrl} alt="Resultado actual" />}
    <span>
      Resultado actual conservado. Se reemplaza sólo si la nueva generación termina bien.
    </span>
    <button
      type="button"
      className="node-reconfigure-cancel"
      onClick={onCancel}
      data-testid={`${testId}-cancel`}
      title="Volver al resultado sin generar nada"
    >
      <X size={11} /> Cancelar
    </button>
  </div>
);
